/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import {
  NearByDestinationsInput,
  DistanceMatrixResponseType,
  NearByDestinationsInfoProps,
  CardArrayType,
  LocationCoords,
  DestinationNodeType,
} from './NearByDestinations.types';
import { NearByDestinationsSkeletonLoader } from './NearByDestinationsSkeletonLoader';
import {
  buildDestinationListWithoutDistance,
  constructOfferSearchURL,
  destinationListResponse,
  filterDestinations,
  generateCardIndexArray,
  getGeoLocationCoordinates,
  getDistanceMatrixForDestinations,
  getHoursAwayText,
  getNearByDestinationsVariables,
  normalizeVariation,
  parseCoordinates,
  resolveTitleWithDestinationName,
} from './utils/helper';
import { useLazyQuery } from '@apollo/client';
import { phoenixNearbyDestinations } from '@marriott/mi-offers-graphql';
import { UXL_ERROR_POLICY } from '../../api/apiConstants';
import { processAcceptLang } from '../../utils/OfferUtils';
import { shouldUseMockData } from '../../utils/CommonUtils';
import { MiniCardCarouselContainer } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

import { INTERNAL_CTA_TYPE, constants } from '../../constants/CommonConstants';
import { useSession } from '@marriott/mi-headless-utils';

export const NearByDestinationsContainer: React.FC<NearByDestinationsInput> = props => {
  const { model, requestId, isAuthorMode } = props;
  const shouldCallGoogleDistanceService = props?.enableGoogleDistanceService;
  const { datalayer } = useSession();
  const destinationDetails = props?.DestinationDetails;
  const [browserLat, setLatitude] = useState(0);
  const [browserLong, setLongitude] = useState(0);
  const [dataLoaderFlag, setDataLoaderFlag] = useState<boolean>(true);

  const acceptLanguage = processAcceptLang(props?.acceptLanguage ?? '');
  const [nearByDestData, setNearByDestData] = useState<NearByDestinationsInfoProps>({
    destinationList: [],
  });
  const [destinationsResult, setDestinationsResult] = useState<any>([]);
  const { envVarsObject } = useClientEnvVarsStore();
  const sourceVariation = normalizeVariation(model?.variation);
  const shouldHideForDestinationMetadata =
    sourceVariation === 'destinationmetadata' &&
    (!destinationDetails || normalizeVariation(destinationDetails?.type) !== 'city');
  const hasValidCoordinates =
    Number.isFinite(browserLat) && Number.isFinite(browserLong) && browserLat !== 0 && browserLong !== 0;

  const getResolvedTitleText = () => {
    return resolveTitleWithDestinationName(props?.title, destinationDetails?.destinationName);
  };

  // Load mock data dynamically for dev and author mode
  const GetMockData = async (isAuthorMode: boolean) => {
    if (shouldUseMockData(isAuthorMode)) {
      const mockData = await import('./__mock__/NearByDestinationsLocations.mock');
      return mockData.default;
    } else {
      return {} as NearByDestinationsInfoProps;
    }
  };
  useEffect(() => {
    const sessionDataLayer = datalayer as Record<string, string | number>;

    if (shouldHideForDestinationMetadata) {
      setLatitude(0);
      setLongitude(0);
      return;
    }

    if (sourceVariation === 'manual' || sourceVariation === 'destinationtag') {
      const authoredCoords = parseCoordinates(model?.lattitude, model?.longtitude);
      setLatitude(authoredCoords.lat);
      setLongitude(authoredCoords.lng);
      return;
    }

    if (sourceVariation === 'destinationmetadata') {
      const destinationCoords = parseCoordinates(destinationDetails?.latitude, destinationDetails?.longitude);
      setLatitude(destinationCoords.lat);
      setLongitude(destinationCoords.lng);
      return;
    }

    if (sourceVariation === 'geolocation') {
      const hasSessionGeoCoordinates =
        sessionDataLayer?.['browser_akamai_loc_lat'] !== undefined &&
        sessionDataLayer?.['browser_akamai_loc_long'] !== undefined;

      if (!hasSessionGeoCoordinates) {
        return;
      }

      const geolocationCoords = parseCoordinates(
        sessionDataLayer?.['browser_akamai_loc_lat'],
        sessionDataLayer?.['browser_akamai_loc_long']
      );

      if (geolocationCoords.lat === 0 || geolocationCoords.lng === 0) {
        return;
      }

      setLatitude(geolocationCoords.lat);
      setLongitude(geolocationCoords.lng);
      return;
    }

    const datalayerCoords = getGeoLocationCoordinates(sessionDataLayer);
    setLatitude(datalayerCoords.lat);
    setLongitude(datalayerCoords.lng);
  }, [
    datalayer,
    model?.variation,
    destinationDetails?.latitude,
    destinationDetails?.longitude,
    destinationDetails?.destinationName,
    destinationDetails?.type,
    model?.lattitude,
    model?.longtitude,
    shouldHideForDestinationMetadata,
    sourceVariation,
  ]);

  useEffect(() => {
    if (shouldHideForDestinationMetadata) {
      setDataLoaderFlag(false);
      setNearByDestData({ destinationList: [] });
      return;
    }

    if (!isAuthorMode && !hasValidCoordinates) {
      setDataLoaderFlag(false);
      setNearByDestData({ destinationList: [] });
      return;
    }

    const inputVariable = getNearByDestinationsVariables(
      browserLat,
      browserLong,
      model?.minDistance ?? '1',
      model?.maxDistance ?? '1'
    );
    const payLoad = {
      variables: {
        ...inputVariable,
      },
    };
    if (isAuthorMode) {
      const fetchMockData = async () => {
        const mockDestinationsData = await GetMockData(isAuthorMode || false);
        setNearByDestData(mockDestinationsData as NearByDestinationsInfoProps);
        mapNearByDestinationsItems(mockDestinationsData as NearByDestinationsInfoProps);
      };

      fetchMockData();
    } else {
      if (hasValidCoordinates) {
        getNearByDestinationsData(payLoad);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserLat, browserLong, hasValidCoordinates, isAuthorMode, shouldHideForDestinationMetadata]);

  const mapNearByDestinationsItems = (destinationList: NearByDestinationsInfoProps) => {
    const destinationsMap = destinationList?.destinationList?.map((card: any, index: number) => {
      const cardLocDetails = model?.trackingProperties?.location
        ? `${model.trackingProperties.location}-card-${index}`
        : `card-${index}`;

      return {
        ':items': {
          [`cardhorizontalmini_copy-${index}`]: {
            trackingProperties: {
              atCCeVar48: null,
              additionalTrackingVariables: null,
              trackingDescription: card?.header,
              description: card?.header,
              clickTrack: model?.trackingProperties?.clickTrack ? true : false,
              impressionTrack: false,
              location: cardLocDetails,
              trackingContentPosition: cardLocDetails,
              merchandisingCategory: model?.trackingProperties?.merchandisingCategory
                ? model.trackingProperties.merchandisingCategory
                : 'nonCobrand',
              impressionCount: false,
              trackingTag: model?.trackingProperties?.trackingTag,
              trackingOfferType: null,
            },
            headerTag: 'h2',
            iconPath: model?.fontIcon,
            clickTrack: model?.trackingProperties?.clickTrack ? true : false,
            header: card.city,
            ctaLink: constructOfferSearchURL(envVarsObject?.['NEXT_PUBLIC_SUBMIT_SEARCH_URL'], card),
            assetVariation: constants.ASSET_VARIATION_ICON,
            componentId: model?.componentId + cardLocDetails,
            descriptionText:
              shouldCallGoogleDistanceService && Number(card?.duration) > 0
                ? getHoursAwayText(card.duration, model?.hour, model?.hours)
                : '',
            isAuthor: false,
            hideEllipsis: false,
            path: '/jcr:content/root/responsivegrid/mi-aem-common-spa/components/content/cardhorizontalmini',
            ':type': 'mi-aem-common-spa/components/content/cardhorizontalmini',
            openInaNewTab: false,
            renderType: 'manual',
            hideDescriptionOnMobile: false,
            wcmMode: model?.wcmMode,
            hideImage: false,
            custom_click_track_value: `${model?.componentId}|${cardLocDetails}-${card.destination}|${INTERNAL_CTA_TYPE}`,
          },
        },
      };
    });

    // Modifying index names to map it with card horizontal mini container cqItems
    const modifiedDestinationArr: CardArrayType = {};
    destinationsMap.forEach((value, index) => {
      modifiedDestinationArr['card-' + index] = value;
    });
    setDestinationsResult(modifiedDestinationArr);
  };

  const processNearByData = async (nearByData: any) => {
    if (nearByData?.offersSearch?.edges?.length) {
      const origins = { lat: browserLat, lng: browserLong };
      const destinations = filterDestinations(
        nearByData.offersSearch.edges,
        model?.minOffersPerCity ?? 1,
        model?.numCitiesToDisplay ?? 1
      );

      if (!shouldCallGoogleDistanceService) {
        const destinationsList = buildDestinationListWithoutDistance(destinations);
        setDataLoaderFlag(false);
        setNearByDestData(destinationsList);
        mapNearByDestinationsItems(destinationsList);
        return;
      }

      const destinationCoordinatesArr: Array<LocationCoords> = [];
      destinations.forEach(dest => {
        const destLatitude = dest.participatingProperties.properties[0].basicInformation.latitude;
        const destLongitude = dest.participatingProperties.properties[0].basicInformation.longitude;
        destinationCoordinatesArr.push({ lat: destLatitude, lng: destLongitude });
      });
      return await getDistanceMatrixForDestinations(origins, destinationCoordinatesArr)
        .then((resp: DistanceMatrixResponseType) => {
          const destinationsList = destinationListResponse(resp, destinations);
          setDataLoaderFlag(false);
          setNearByDestData(destinationsList);
          mapNearByDestinationsItems(destinationsList);
        })
        .catch(error => {
          // Fallback to rendering destination cards without Google distance metadata.
          const fallbackDestinationsList = buildDestinationListWithoutDistance(destinations);
          setDataLoaderFlag(false);
          setNearByDestData(fallbackDestinationsList);
          mapNearByDestinationsItems(fallbackDestinationsList);
          console.log(`[NearByDestinations] Distance service failed. Rendering fallback list. Error: ${error}`);
        });
    } else {
      setDataLoaderFlag(false);
      return { destinationList: [] };
    }
  };

  const handleNearByDataError = (error: any) => {
    setDataLoaderFlag(false);
    console.log('handleNearByDataError', error);
  };

  const [getNearByDestinationsData, { data: nearByData, loading: isLoading, called: queryCalled }] = useLazyQuery(
    phoenixNearbyDestinations,
    {
      fetchPolicy: 'network-only',
      errorPolicy: UXL_ERROR_POLICY,
      context: {
        headers: {
          'x-request-id': requestId + '-o-offers-nearbydestinations',
          'accept-language': acceptLanguage,
        },
      },
      onCompleted: processNearByData,
      onError: handleNearByDataError,
    }
  );

  useEffect(() => {
    if (queryCalled && !isLoading && !nearByData) {
      setDataLoaderFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryCalled, isLoading]);

  if (shouldHideForDestinationMetadata) {
    return null;
  }

  if (!isAuthorMode && !hasValidCoordinates) {
    return null;
  }

  return (
    <div>
      {(!isLoading && !dataLoaderFlag) || isAuthorMode ? (
        nearByDestData?.destinationList?.length > 0 && (
          <MiniCardCarouselContainer
            titleText={getResolvedTitleText()}
            subTitleText={''}
            ctaLabel={''}
            ctaLink={''}
            eyebrow={''}
            ctaType={''}
            cardsCount={nearByDestData.destinationList.length}
            openInaNewTab={false}
            totalNumberOfCards={generateCardIndexArray(nearByDestData.destinationList.length)}
            trackingProperties={model?.trackingProperties ?? {}}
            componentId={model?.componentId ?? 'componentId'}
            pagePath={''}
            itemPath={''}
            wcmMode={model?.wcmMode ?? 'edit'}
            isAuthorMode={isAuthorMode ?? false}
            cqItems={destinationsResult}
            isFullWidthCards={true}
          />
        )
      ) : (
        <NearByDestinationsSkeletonLoader />
      )}
    </div>
  );
};
